/**
 * Client-safe matching for the hub search combobox over the static index (`search-index.ts`).
 * Diacritic-folded and case-insensitive. Tiers, best first: name prefix, word prefix, substring
 * (3+ characters), then sport keywords. At most `limit` results, grouped Spots, Resorts, Areas,
 * Sports. No scores: a score needs per-request freshness.
 */
import { HUB_SPORT_GROUPS } from "./sports";
import type {
  HubSearchGroup,
  HubSearchIndex,
  HubSearchOutcome,
  HubSearchResult,
  HubSearchResultKind,
  HubSport,
} from "./types";

export const HUB_SEARCH_LIMIT = 8;

const GROUP_LABEL: Readonly<Record<HubSearchResultKind, string>> = {
  spot: "Spots",
  resort: "Resorts",
  area: "Areas",
  sport: "Sports",
};
const KIND_ORDER: readonly HubSearchResultKind[] = ["spot", "resort", "area", "sport"];

type Folded = Readonly<{ text: string; index: readonly number[] }>;

/** Lower case, accents removed, punctuation as spaces; `index` maps each folded character back. */
function foldWithIndex(value: string): Folded {
  let text = "";
  const index: number[] = [];
  let offset = 0;
  for (const ch of value) {
    const folded = ch.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/[^\p{L}\p{N}]/gu, " ");
    for (const out of folded) {
      text += out;
      index.push(offset);
    }
    offset += ch.length;
  }
  return { text, index };
}

/** The folded form used for matching ("Val d'Isère" to "val d isere"). */
export function foldSearchText(value: string): string {
  return foldWithIndex(value).text.replace(/\s+/gu, " ").trim();
}

type Tier = 0 | 1 | 2 | 3;
type NameMatch = Readonly<{ tier: Tier; range: readonly [number, number] | null }>;

function isWordStart(text: string, at: number): boolean {
  return at === 0 || text[at - 1] === " ";
}

/** Match a folded query against a name: prefix (0), word prefix (1), substring of 3+ (2). */
function matchName(name: string, query: string): NameMatch | null {
  const folded = foldWithIndex(name);
  const text = folded.text;
  const toRange = (at: number): readonly [number, number] => {
    const start = folded.index[at] ?? 0;
    const last = folded.index[at + query.length - 1] ?? start;
    let end = name.length;
    for (let i = at + query.length; i < folded.index.length; i += 1) {
      if (folded.index[i] > last) {
        end = folded.index[i];
        break;
      }
    }
    return [start, end];
  };
  if (text.startsWith(query)) return { tier: 0, range: toRange(0) };
  let at = text.indexOf(query, 1);
  let substring = -1;
  while (at !== -1) {
    if (isWordStart(text, at)) return { tier: 1, range: toRange(at) };
    if (substring === -1) substring = at;
    at = text.indexOf(query, at + 1);
  }
  if (query.length >= 3 && substring !== -1) return { tier: 2, range: toRange(substring) };
  return null;
}

function matchAliases(aliases: readonly string[], query: string): Tier | null {
  let best: Tier | null = null;
  for (const alias of aliases) {
    const match = matchName(alias, query);
    if (match && (best === null || match.tier < best)) best = match.tier;
  }
  return best;
}

function matchKeywords(keywords: string, query: string): boolean {
  if (query.length < 2) return false;
  const text = ` ${foldSearchText(keywords)}`;
  return text.includes(` ${query}`);
}

type Scored = Readonly<{ result: HubSearchResult; tier: Tier; exact: boolean }>;

function compareScored(a: Scored, b: Scored): number {
  return Number(b.exact) - Number(a.exact)
    || a.tier - b.tier
    || KIND_ORDER.indexOf(a.result.kind) - KIND_ORDER.indexOf(b.result.kind)
    || a.result.name.length - b.result.name.length
    || a.result.name.localeCompare(b.result.name, "en-GB");
}

const GROUP_LABELS: ReadonlyMap<string, string> = new Map(HUB_SPORT_GROUPS.map((group) => [group.key, group.label]));

/**
 * Search the index. `sports` are the sports the hub shows (named paddle profiles only when
 * public); spots and areas are not filtered.
 */
export function searchHubIndex(
  index: HubSearchIndex,
  rawQuery: string,
  sports: readonly HubSport[],
  limit: number = HUB_SEARCH_LIMIT,
): HubSearchOutcome {
  const query = foldSearchText(rawQuery);
  if (!query) return { groups: [], shown: 0, total: 0 };
  const scored: Scored[] = [];
  const country = (code: string) => index.countries[code] ?? code;

  for (const spot of index.spots) {
    const match = matchName(spot.name, query);
    if (!match) continue;
    const place = country(spot.countryCode);
    const resort = spot.type === "ski";
    const what = resort ? "Resort" : spot.type === "sailing" ? "Sailing venue" : "Surf spot";
    scored.push({
      tier: match.tier,
      exact: foldSearchText(spot.name) === query,
      result: {
        kind: resort ? "resort" : "spot",
        id: spot.slug,
        name: spot.name,
        secondary: `${place} · ${what}`,
        secondaryPhone: `${place} · ${resort ? "Resort" : spot.type === "sailing" ? "Sailing" : "Surf"}`,
        href: spot.href,
        sportKey: null,
        icon: resort ? "piste" : spot.type === "sailing" ? "sailing" : "surf",
        match: match.range,
      },
    });
  }

  for (const area of index.areas) {
    const match = matchName(area.name, query);
    const aliasTier = match ? null : matchAliases(area.aliases, query);
    if (!match && aliasTier === null) continue;
    const coast = area.kind === "coastal";
    const members = `${area.memberCount} ${coast ? (area.memberCount === 1 ? "surf spot" : "surf spots") : (area.memberCount === 1 ? "resort" : "resorts")}`;
    scored.push({
      tier: match ? match.tier : (aliasTier ?? 2),
      exact: foldSearchText(area.name) === query,
      result: {
        kind: "area",
        id: `${area.sport}:${area.slug}`,
        name: area.name,
        secondary: `${coast ? "Coast" : "Mountain"} area · ${members}`,
        secondaryPhone: `${coast ? "Coast" : "Mountains"} · ${members}`,
        href: area.href,
        sportKey: null,
        icon: coast ? "coast" : "mountain",
        match: match ? match.range : null,
      },
    });
  }

  for (const sport of sports) {
    const match = matchName(sport.label, query);
    const keyword = match ? false : matchKeywords(sport.keywords, query);
    if (!match && !keyword) continue;
    const group = GROUP_LABELS.get(sport.group) ?? sport.group;
    const beta = sport.beta ? " · Beta" : "";
    scored.push({
      tier: match ? match.tier : 3,
      exact: foldSearchText(sport.label) === query,
      result: {
        kind: "sport",
        id: sport.key,
        name: sport.label,
        secondary: `Sport · ${group}${beta}`,
        secondaryPhone: `${group} · Sport${beta}`,
        href: null,
        sportKey: sport.key,
        icon: sport.icon,
        match: match ? match.range : null,
      },
    });
  }

  const top = [...scored].sort(compareScored).slice(0, Math.max(0, limit));
  const groups: HubSearchGroup[] = KIND_ORDER.flatMap((kind) => {
    const results = top.filter((entry) => entry.result.kind === kind).map((entry) => entry.result);
    return results.length > 0 ? [{ kind, label: GROUP_LABEL[kind], results }] : [];
  });
  return { groups, shown: top.length, total: scored.length };
}
